/**
 * The composer's confirm step (spec 8.3) — Phase 2B, Task 13.
 *
 * A sibling file rather than more keys in `reviewRequests.composer.ts` for the
 * reason `reviewRequests.scholars.ts` exists: that file was 373 lines against
 * this repo's 400-line hard limit before this step needed a sender line, a
 * fifth breakdown bucket and a seven-status submit bucket. The whole `confirm`
 * object moved here and is spread back into the SAME `reviewRequests.requests`
 * namespace, so every key below is still `reviewRequests.requests.confirm.*`
 * and no import path anywhere changed.
 *
 * Same two constraints as its siblings: English only, and `{named}`
 * placeholders only — `cspSafeMessageCompiler` cannot parse vue-i18n's `|`
 * plural syntax, so count-dependent text is a manual `_one` / `_other` pair.
 *
 * ## The purchase-order sentence lives HERE, and the summary panel borrows it
 *
 * `summary.purchaseOrdersValue_one/_other` used to say the same thing in
 * different words ("… — one for every scholar on the request."), and the panel
 * sits on screen BESIDE this step: two wordings of one money claim, side by
 * side, is exactly the confusion spec 8.3 exists to prevent. Those two keys are
 * retired; `composerRules.ts::purchaseOrderLine` now renders
 * `purchaseOrders_one/_other` below, which is also where it already read
 * `purchaseOrdersNone` from for the trigger-off case. One sentence, one key,
 * two places — so they cannot disagree.
 */
export const reviewRequestsConfirmMessages = {
  // Spec 8.3: a plain list of what is about to happen, with the irreversible
  // line unmissable, plus the isolation guarantee stated in words because it
  // is the property the SAM is being trusted to preserve.
  confirm: {
    title: 'Confirm and send',
    intro: 'This is what sending this request will do.',
    folderLine: 'A review folder will be created in {folder}.',
    // `{total}` is `documentRules.ts::pageCountTotal`'s output, already
    // rendered, rather than a `{documents}`/`{pages}` pair: BOTH halves are
    // count-dependent, and a one-document request read "1 documents · 1 pages"
    // with the pair — the exact bug `pageCount.total` was split up to fix.
    documentsLine: '{total} · band {band}',
    scholarsLine_one: '1 scholar will be asked to review.',
    scholarsLine_other: '{n} scholars will be asked to review.',
    // Spec 6.3: the mail genuinely leaves the SAM's own mailbox, not a system
    // address, and `ReviewRequestResponse.sender_email` carries it for exactly
    // this line. Said BEFORE it goes, because afterwards it cannot be unsent.
    senderLine:
      'Scholars will see this arrive from {email} — your own mailbox, not a system address.',
    purchaseOrders_one: '1 purchase order — {breakdown} · PO trigger is ON.',
    purchaseOrders_other: '{n} purchase orders — {breakdown} · PO trigger is ON.',
    // With the trigger off Odoo is not called at all, and the SAM must not
    // be told otherwise (spec 5.3).
    purchaseOrdersNone: "No purchase order will be raised — this client's PO trigger is off.",
    breakdownBand: '{n} category {band}',
    breakdownFixed: '{n} fixed value',
    breakdownZero: '{n} zero value',
    // A scholar the resolver has not priced — blocked, or carrying no
    // `pricing_basis`. Unreachable on this step (`canAdvance` holds `confirm`
    // shut until every scholar resolves), but the summary panel renders the
    // same sentence from the first scholar onward, when some of them still
    // are. Naming them "not yet priced" is the honest answer; folding them
    // into `breakdownZero` would be a false money claim.
    breakdownUnpriced: '{n} not yet priced',
    // No scholars yet — the panel's own first render. Says where the number
    // comes from instead of leaving the dash dangling on an empty list.
    breakdownNone: 'one for every scholar on the request',
    isolation:
      "Each scholar gets a link to their own copy of these documents. No scholar can see another scholar's copy, their comments, or that anyone else was asked.",
    irreversible: 'Sending cannot be undone. The request can be cancelled, but not unsent.',
    send: 'Send request',
    sending: 'Sending…',

    // The step's own refusal reuses the pre-landed
    // `reviewRequests.requests.submitBlocked`, which was landed for exactly
    // this and has no other caller — it is not repeated here, because two
    // copies of one refusal is how the rail and the step start disagreeing
    // about whether a request may be sent.

    // Mapped from the STATUS `submitDraft` answers with, never from the
    // server's own text: only a 409 survives `ApiService`
    // (`reviewRequests.http.ts`), and this route answers 422 to five different
    // refusals whose bodies are the server's own English.
    //
    // `forbidden` is the one refusal a SAM can act on themselves, and it is
    // deliberately NOT collapsed into `fallback`: spec 3.2 makes creating the
    // review folder with the SAM's own delegated token the authorization
    // check, so a 403 here is Microsoft saying they cannot write to the folder
    // they chose — which they fix by asking for access or choosing another.
    // The submit SUCCEEDED and only the navigation to the sent request did
    // not. Never the generic retry copy: the request is already permanent, so
    // the one thing this must not invite is a second send.
    sentNavigationFailed:
      'This request was sent, but its page could not be opened. Reload the page to see it — do not send it again.',
    submitStatus: {
      expired: 'Your SharePoint sign-in has expired. Reload the page and try again.',
      forbidden:
        'You do not have permission to create a review folder inside the folder you chose. Ask for write access to it, or go back a step and choose a different folder.',
      notFound: 'This request could not be found. It may have been cancelled.',
      conflict:
        'This request has already been sent. Open it to see its current state — do not send it again.',
      unprocessable:
        'This request cannot be sent yet. Check that every scholar resolves and at least one document is selected, then try again.',
      unavailable:
        'SharePoint is not configured on this server, so the review folder cannot be created. Ask an administrator.',
      fallback: 'This request could not be sent. Please try again.',
    },
  },
}
